"use client";

import { type ReactNode, useId, useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  type Option,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  LABEL,
  NOTICE,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  type CitationParts,
  partsToHtml,
  partsToPlain,
} from "@/lib/tool-kit/citation-parts";

/**
 * The form-and-results shell behind the hand-built citation generators.
 *
 * A generator supplies three things: the fields its source type needs, a pure
 * `format(values)` that turns them into APA / MLA / Chicago output, and its strings.
 * This component draws the form, re-formats on every keystroke, and shows each style's
 * entries with their italics, a rich-text copy button (italics survive the paste into
 * Word or Google Docs) and a plain-text fallback.
 *
 * Keeping the formatting pure and outside the component is deliberate: it is what the
 * generator's unit test calls, so the examples on the page and the output of the widget
 * are checked against the same function.
 */

export type SourceValues = Record<string, string>;

export type SourceFieldKind =
  | "text"
  | "textarea"
  | "people"
  | "date"
  | "select"
  | "checkbox"
  | "url";

export interface SourceField {
  /** Key in `SourceValues`. A `checkbox` stores "true" or "". */
  id: string;
  kind: SourceFieldKind;
  label: string;
  placeholder?: string;
  hint?: string;
  /** For `select`. */
  options?: readonly Option[];
  /** Full width on wide screens (textareas and people lists usually are). */
  wide?: boolean;
}

export interface CitationEntry {
  /** "Reference list", "In-text (parenthetical)", "Footnote", "Bibliography"… */
  label: string;
  parts: CitationParts;
}

export interface CitationOutput {
  /** Stable key: "apa", "mla", "chicago". */
  key: string;
  /** "APA 7th edition" — from the widget's strings. */
  label: string;
  entries: CitationEntry[];
  /** An optional style-specific tip shown under the entries. */
  note?: string;
}

export interface SourceCitationStrings {
  formHeading: string;
  resultsHeading: string;
  emptyPrompt: string;
  copy: string;
  copied: string;
  tryExample: string;
  clear: string;
}

function FieldControl({
  field,
  id,
  value,
  onChange,
}: {
  field: SourceField;
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  if (field.kind === "checkbox") {
    return (
      <div className="space-y-1">
        <Checkbox
          checked={value === "true"}
          label={field.label}
          onChange={(checked) => onChange(checked ? "true" : "")}
        />
        {field.hint ? <p className={HINT}>{field.hint}</p> : null}
      </div>
    );
  }
  if (field.kind === "select") {
    return (
      <SelectField
        hint={field.hint}
        id={id}
        label={field.label}
        onChange={onChange}
        options={field.options ?? []}
        value={value}
      />
    );
  }
  if (field.kind === "textarea" || field.kind === "people") {
    return (
      <TextAreaField
        hint={field.hint}
        id={id}
        label={field.label}
        onChange={onChange}
        placeholder={field.placeholder}
        prose
        rows={field.kind === "people" ? 3 : 4}
        value={value}
      />
    );
  }
  return (
    <TextField
      hint={field.hint}
      id={id}
      label={field.label}
      onChange={onChange}
      placeholder={field.placeholder}
      type={field.kind === "url" ? "url" : "text"}
      value={value}
    />
  );
}

function Parts({ parts }: { parts: CitationParts }) {
  return (
    <>
      {parts.map((part, index) =>
        part.italic ? (
          // Parts are positional fragments of one sentence.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional fragments
          <i key={index}>{part.text}</i>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: positional fragments
          <span key={index}>{part.text}</span>
        )
      )}
    </>
  );
}

function OutputCard({
  output,
  strings,
}: {
  output: CitationOutput;
  strings: SourceCitationStrings;
}) {
  return (
    <section className={`${CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>{output.label}</h3>
      {output.entries.map((entry) => (
        <div className="space-y-2" key={entry.label}>
          <p className={LABEL}>{entry.label}</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <p className="break-words pl-6 -indent-6 text-foreground leading-relaxed">
              <Parts parts={entry.parts} />
            </p>
            <div className="shrink-0">
              <CopyButton
                copiedLabel={strings.copied}
                html={partsToHtml(entry.parts)}
                label={strings.copy}
                small
                text={partsToPlain(entry.parts)}
              />
            </div>
          </div>
        </div>
      ))}
      {output.note ? (
        <p className="text-muted-foreground text-sm">{output.note}</p>
      ) : null}
    </section>
  );
}

export function SourceCitationBuilder({
  fields,
  format,
  strings,
  example,
  isReady,
  intro,
}: {
  fields: readonly SourceField[];
  /** Pure: values in, one `CitationOutput` per style out. */
  format: (values: SourceValues) => CitationOutput[];
  strings: SourceCitationStrings;
  /** Values the "Try an example" button fills in. */
  example: SourceValues;
  /** True once enough is entered to show output — usually "has a title". */
  isReady: (values: SourceValues) => boolean;
  /** Optional content above the form (a mode switch, a short note). */
  intro?: ReactNode;
}) {
  const prefix = useId();
  const [values, setValues] = useState<SourceValues>({});
  const outputs = useMemo(
    () => (isReady(values) ? format(values) : []),
    [values, format, isReady]
  );

  function update(id: string, value: string) {
    setValues((current) => ({ ...current, [id]: value }));
  }

  return (
    <>
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={WIDGET_H2}>{strings.formHeading}</h2>
          <div className="flex flex-wrap gap-2">
            <button
              className={BUTTON_SECONDARY}
              onClick={() => setValues({ ...example })}
              type="button"
            >
              {strings.tryExample}
            </button>
            <button
              className={BUTTON_SECONDARY}
              disabled={Object.values(values).every((value) => value === "")}
              onClick={() => setValues({})}
              type="button"
            >
              {strings.clear}
            </button>
          </div>
        </div>
        {intro}
        <div className="grid gap-4 sm:grid-cols-2">
          {fields.map((field) => (
            <div
              className={
                field.wide || field.kind === "textarea" ? "sm:col-span-2" : ""
              }
              key={field.id}
            >
              <FieldControl
                field={field}
                id={`${prefix}-${field.id}`}
                onChange={(value) => update(field.id, value)}
                value={values[field.id] ?? ""}
              />
            </div>
          ))}
        </div>
      </section>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {outputs.length === 0 ? (
          <p className={NOTICE}>{strings.emptyPrompt}</p>
        ) : (
          outputs.map((output) => (
            <OutputCard key={output.key} output={output} strings={strings} />
          ))
        )}
      </section>
    </>
  );
}
